import { Fragment, useMemo, useRef, useState } from 'react';

import { InfoOutlined } from '@mui/icons-material';
import { Box, Tooltip } from '@mui/material';
import { getIn, useFormikContext } from 'formik';

import { CissNumberField } from 'components/fields';
import { ConfirmModal } from 'components/modal';
import { SubTitle } from 'components/text';
import { DreFormGroup } from 'pages/financeiro/dre/components/templates/DreFormGroup';
import { DreFormRow } from 'pages/financeiro/dre/components/templates/DreFormRow';
import { useRecalculateDre } from 'pages/financeiro/dre/hooks';
import { DreCustosConfiguracao, DreForm } from 'pages/financeiro/dre/types';
import { getLines } from 'pages/financeiro/dre/utils';

type ModalCustos = {
    title: string;
    description: string;
    formula: string;
};

export function DreFormCustos() {
    const { errors, values, setFieldValue } = useFormikContext<DreForm>();
    const [openModal, setOpenModal] = useState<boolean>(false);
    const modalData = useRef<ModalCustos | null>(null);
    const { recalculateValidatedDre } = useRecalculateDre();

    return useMemo(
        () => (
            <DreFormGroup label="Custos" id="2">
                {values.dreMercadologicaCustos.map((custos, index) => (
                    <Fragment key={custos.dreMercadologicaGrupoExibicao.idDreMercadologicaGrupoExibicao}>
                        <SubTitle
                            sx={{ m: 0 }}
                            id={`2_${custos.dreMercadologicaGrupoExibicao.idDreMercadologicaGrupoExibicao}`}
                            label={<strong>{custos.dreMercadologicaGrupoExibicao.dsDreMercadologicaGrupoExibicao}</strong>}
                        />

                        {Object.entries(getLines<DreCustosConfiguracao>(custos.dreMercadologicaValor, 'dreMercadologicaConfigCusto')).map(
                            ([line, configuracoes]) => {
                                return (
                                    <DreFormRow key={line}>
                                        {configuracoes.map((config) => {
                                            const name = `dreMercadologicaCustos[${index}].dreMercadologicaValor[${config.index}].vlTotal`;
                                            const isFormula = config.dreMercadologicaConfigCusto.dreTipoPreenchimento.idDreTipoPreenchimento === 1;
                                            const isPercentual = config.dreMercadologicaConfigCusto.fgPercentual;

                                            return (
                                                <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1.5 }} key={name}>
                                                    <CissNumberField
                                                        name={name}
                                                        inputType="decimal"
                                                        disabled={isFormula}
                                                        value={config.vlTotal}
                                                        maxValue={isPercentual ? 100 : undefined}
                                                        forceMaxValue={isPercentual}
                                                        error={getIn(errors, name)}
                                                        onBlur={recalculateValidatedDre}
                                                        onChange={(e) => setFieldValue(name, Number(e.target.value))}
                                                        label={`${config.dreMercadologicaConfigCusto.dsNomeExibicao} (${isPercentual ? '%' : 'R$'})`}
                                                    />

                                                    <Tooltip
                                                        title="Clique para saber mais"
                                                        sx={{ cursor: 'pointer' }}
                                                        onClick={() => {
                                                            setOpenModal(true);

                                                            modalData.current = {
                                                                title: config.dreMercadologicaConfigCusto.dsNomeExibicao,
                                                                description: config.dreMercadologicaConfigCusto.dsObservacao || 'Não informado',
                                                                formula: config.dreMercadologicaConfigCusto.dsValorConfig || 'Não informado',
                                                            };
                                                        }}
                                                    >
                                                        <InfoOutlined />
                                                    </Tooltip>
                                                </Box>
                                            );
                                        })}
                                    </DreFormRow>
                                );
                            },
                        )}
                    </Fragment>
                ))}

                <ConfirmModal
                    confirmText="Entendi"
                    open={openModal}
                    showCancel={false}
                    setOpen={setOpenModal}
                    title={modalData.current?.title}
                    text={`Descrição: ${modalData.current?.description}\nFórmula: ${modalData.current?.formula}`}
                />
            </DreFormGroup>
        ),
        [values.dreMercadologicaCustos, errors.dreMercadologicaCustos, openModal],
    );
}
